import * as ApiConstant from '../foundation/apiConstant';
import { Router } from 'vue-router';
import { useUrlBuilder } from './useUrlBuilder';

type NavigationOptions = { replace?: boolean; forceUpdate?: boolean };

/**
 * Custom navigation to cater for vue instances being located in different places in the sitecore website.
 *
 * @param router
 * @returns
 */
export function useUrlNavigator(router: Router) {
  const urlBuilder = useUrlBuilder();

  /**
   * Navigate to the url using vue router for local development. For production builds it will do a full page reload to the page where the destination component is being hosted.
   *
   * @param url        The path (for local development)eg. /search-results or /car-details. Or the full
   *                   url https://racqcm.localhost/path1/path2/.../page-with-vue-instance for the
   *                   production build as the components maybe in different locations.
   * @param options    Additional options to have finer control over routing.
   *
   *
   */
  function navigateTo(url: string, options: NavigationOptions = { replace: false }) {
    const internalRouter = router;

    if (ApiConstant.isProduction || options.forceUpdate) {
      window.location.href = url;
    } else {
      if (options.replace) {
        return internalRouter.replace(url);
      }

      return internalRouter.push(url);
    }
  }

  /**
   * Go to the car detail page
   */
  function gotoCarDetail(vehicleId: string, year: string, make: string, model: string, options: NavigationOptions = { forceUpdate: true }) {
    const carDetailUrl = urlBuilder.getCarDetailsUrl(vehicleId, year, make, model);
    navigateTo(carDetailUrl, options);
  }

  /**
   * Go to search results page
   */
  function gotoSearchResults() {
    return navigateTo(urlBuilder.getSearchResultsUrl({ fromComparisonPage: true, useCustomPath: true }));
  }

  /**
   * Go to home page with the search results modal open
   */
  function gotoHomeWithSearchOpen() {
    return navigateTo(urlBuilder.getHomePageUrl({ searchModalOpen: true }), {});
  }

  /**
   * Go to comparison page
   */
  function getComparisonPageUrl(vehicleIds: string[]) {
    return urlBuilder.getCarComparisonUrl(vehicleIds);
  }

  function gotoExpertReviews(carId: string) {
    return navigateTo(urlBuilder.getExpertReviewsUrl(carId));
  }

  return {
    navigateTo,
    gotoCarDetail,
    gotoExpertReviews,
    gotoSearchResults,
    getComparisonPageUrl,
    gotoHomeWithSearchOpen,
  };
}
